const SORT_BOTTOM = new Set([null, undefined, '', '-', 'zero']);
const isBottomVal = (v) => SORT_BOTTOM.has(v) || v === 0;
const isBottomNum = (v) => {
  if (SORT_BOTTOM.has(v)) return true;
  const n = Number(v);
  return isNaN(n) || n === 0;
};

const MONTHS = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
  january: 0, february: 1, march: 2, april: 3, june: 5,
  july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
};

/**
 * Parses common date formats used across the app into a Unix timestamp.
 * Handles: Date objects, ISO strings, "20-Feb-25" (DD-Mon-YY),
 * "June-15-26" (Mon-DD-YY), and anything Date.parse can read.
 * Returns null for null/empty/dash/sentinel "1969-12-31".
 */
export const parseDateValue = (v) => {
  if (v == null || v === '' || v === '-' || v === '1969-12-31') return null;
  if (v instanceof Date) return isNaN(v) ? null : v.getTime();
  const s = String(v).trim();

  let m = s.match(/^(\d{1,2})-([A-Za-z]{3,})-(\d{2,4})$/);
  if (m) {
    const day = Number(m[1]);
    const mon = MONTHS[m[2].toLowerCase()];
    let year = Number(m[3]);
    if (year < 100) year += 2000;
    if (mon != null && !isNaN(day) && !isNaN(year)) return new Date(year, mon, day).getTime();
  }

  m = s.match(/^([A-Za-z]{3,})-(\d{1,2})-(\d{2,4})$/);
  if (m) {
    const mon = MONTHS[m[1].toLowerCase()];
    const day = Number(m[2]);
    let year = Number(m[3]);
    if (year < 100) year += 2000;
    if (mon != null && !isNaN(day) && !isNaN(year)) return new Date(year, mon, day).getTime();
  }

  const t = Date.parse(s);
  return isNaN(t) ? null : t;
};

/**
 * Wraps a Tabulator sorter so that empty/null/zero values always sort to the
 * bottom regardless of sort direction. Also supports the string shorthand
 * 'date' (parses common app date formats first).
 *
 * @param {string|Function} sorter - 'number' | 'string' | 'date' | custom fn
 */
export const wrapSorter = (sorter) => {
  const isDate = sorter === 'date';
  const isNumber = sorter === 'number';
  const baseFn =
    typeof sorter === 'function'
      ? sorter
      : isNumber
        ? (a, b) => Number(a) - Number(b)
        : isDate
          ? (a, b) => {
              const ta = parseDateValue(a);
              const tb = parseDateValue(b);
              if (ta == null && tb == null) return 0;
              if (ta == null) return -1;
              if (tb == null) return 1;
              return ta - tb;
            }
          : (a, b) => String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' });

  const isEmpty = isDate
    ? (v) => parseDateValue(v) == null
    : isNumber
      ? isBottomNum
      : isBottomVal;

  return (a, b, aRow, bRow, col, dir, params) => {
    const aE = isEmpty(a);
    const bE = isEmpty(b);
    if (aE && bE) return 0;
    if (aE) return dir === 'asc' ? 1 : -1;
    if (bE) return dir === 'asc' ? -1 : 1;
    return baseFn(a, b, aRow, bRow, col, dir, params);
  };
};

/**
 * Maps over a Tabulator columns array and wraps every defined sorter with
 * wrapSorter. Columns without a sorter are returned untouched. Use this once
 * at the point columns are passed to `new Tabulator({ columns: ... })`.
 */
export const wrapColumns = (columns) =>
  (columns || []).map((col) =>
    col && col.sorter != null ? { ...col, sorter: wrapSorter(col.sorter) } : col
  );
